import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { APPOINTMENT_STATUS, formatDateTime } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  CalendarDays, Clock, Users2, CheckCircle2, Stethoscope,
  LogIn, ChevronRight, Activity,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-navy/10 bg-white px-4 py-3 shadow-sm">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent || "bg-navy/5"}`}>
        <Icon className="h-4 w-4 text-navy" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-bold text-navy">{value}</p>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = APPOINTMENT_STATUS.find((x) => x.value === status) || APPOINTMENT_STATUS[0];
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${s.color}`}>{s.label}</span>;
}

export default function DoctorMyDay() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = useCallback(() => {
    api.get("/doctor/my-day")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // My Day is the doctor's one home, so their own running totals live here
  // rather than on a separate analytics page. The endpoint scopes itself to the
  // caller when they're a doctor and strips revenue server-side. Failure is
  // silent — this block is supplementary, the queue below is the page.
  useEffect(() => {
    api.get("/reports/doctor-performance")
      .then((r) => setPerf(r.data?.doctors?.[0] || null))
      .catch(() => {});
  }, []);
  // useLiveEvents takes ONE callback. This was called with an array as the
  // handler, so every event threw a swallowed TypeError and this page never
  // actually live-refreshed. It matters now: reception checking a patient in
  // has to appear on the doctor's board.
  useLiveEvents((e) => { if (e?.type === "appointment.changed") load(); });

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startConsultation = (apptId) => {
    navigate(`/consultation/${apptId}`);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" /></div>;
  if (!data) return <p className="py-12 text-center text-muted-foreground">Could not load schedule.</p>;

  const { appointments, stats } = data;
  const niceDate = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  // Find the "next" patient: first checked_in, or first scheduled/confirmed
  const nextAppt = appointments.find((a) => a.status === "checked_in")
    || appointments.find((a) => a.status === "in_consultation")
    || appointments.find((a) => ["scheduled", "confirmed"].includes(a.status));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">My Day</p>
        <h2 className="font-display text-2xl font-semibold text-navy">{niceDate}</h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard icon={CalendarDays} label="Total Today" value={stats.total_today} />
        <StatCard icon={CheckCircle2} label="Seen" value={stats.seen} accent="bg-green-50" />
        <StatCard icon={Stethoscope} label="In Consultation" value={stats.in_consultation} accent="bg-indigo-50" />
        <StatCard icon={Clock} label="Waiting" value={stats.waiting} accent="bg-amber-50" />
        <StatCard icon={Activity} label="Avg Wait" value={stats.avg_wait_minutes ? `${stats.avg_wait_minutes}m` : "—"} />
      </div>

      {/* Running totals — the doctor's own numbers, no revenue. */}
      {perf && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">My Practice</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard icon={CalendarDays} label="Appointments This Month" value={perf.month_appointments} />
            <StatCard icon={Users2} label="Patients Treated" value={perf.unique_patients} accent="bg-green-50" />
            <StatCard icon={Stethoscope} label="Total Consultations" value={perf.total_consultations} accent="bg-indigo-50" />
            <StatCard icon={CheckCircle2} label="Completion Rate" value={`${perf.completion_rate}%`} accent="bg-emerald-50" />
          </div>
        </div>
      )}

      {/* Appointment queue */}
      <div className="rounded-xl border border-navy/10 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-navy/10 px-5 py-3">
          <h3 className="font-display font-semibold text-navy">Today's Queue</h3>
          <p className="text-xs text-muted-foreground">{appointments.length} appointment{appointments.length !== 1 ? "s" : ""}</p>
        </div>

        {appointments.length === 0 ? (
          <p className="px-5 py-12 text-center text-sm text-muted-foreground">No appointments scheduled for today.</p>
        ) : (
          <div className="divide-y divide-navy/5">
            {appointments.map((a) => {
              const isNext = nextAppt && a.id === nextAppt.id;
              const time = a.scheduled_at ? new Date(a.scheduled_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";
              const patientName = a.patient?.name || a.patient_name || "Patient";
              const patientAge = a.patient?.age;
              const patientGender = a.patient?.gender;
              const ageLine = [patientAge, patientGender].filter(Boolean).join("/");

              return (
                <div
                  key={a.id}
                  className={`flex items-center gap-4 px-5 py-3.5 transition-colors ${isNext ? "bg-gold/5 border-l-4 border-l-gold" : "hover:bg-nude/30"}`}
                >
                  {/* Time */}
                  <div className="w-16 flex-shrink-0 text-center">
                    <p className="text-sm font-semibold text-navy">{time}</p>
                    <p className="text-[10px] text-muted-foreground">{a.duration_minutes || 60}min</p>
                  </div>

                  {/* Patient info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-ink">{patientName}</p>
                      {isNext && <span className="rounded bg-gold/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-gold">Next</span>}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {ageLine && `${ageLine} · `}{a.service || "General"}{a.patient?.mobile ? ` · ${maskPhone(a.patient.mobile)}` : ""}
                    </p>
                  </div>

                  {/* Status */}
                  <StatusBadge status={a.status} />

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {(a.status === "scheduled" || a.status === "confirmed") && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/checkin/${a.id}`)} className="border-navy/20 text-navy">
                        <LogIn className="mr-1 h-3.5 w-3.5" /> Check In
                      </Button>
                    )}
                    {a.status === "checked_in" && (
                      <Button size="sm" onClick={() => startConsultation(a.id)} className="bg-navy text-white hover:bg-navy-hover">
                        <Stethoscope className="mr-1 h-3.5 w-3.5" /> Start
                      </Button>
                    )}
                    {a.status === "in_consultation" && (
                      <Button size="sm" variant="outline" onClick={() => startConsultation(a.id)} className="border-navy/20 text-navy">
                        <ChevronRight className="mr-1 h-3.5 w-3.5" /> Resume
                      </Button>
                    )}
                    {(a.status === "checked_out" || a.status === "completed") && (
                      <span className="text-xs font-medium text-cyan-700">With reception</span>
                    )}
                    {a.status === "visited" && (
                      <span className="text-xs font-medium text-green-600">Done</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
